import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, RefreshCw, Server } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getEdiPartners, createEdiPartner, updateEdiPartner, deleteEdiPartner } from '../../config/api';

const PARTNER_TYPES = ['WHOLESALER', 'MANUFACTURER', 'GPO', 'DISTRIBUTOR'];
const TX_SETS = ['844', '845', '849', '997'];

const emptyForm = {
  name: '',
  partnerType: 'WHOLESALER',
  isaQualifier: 'ZZ',
  isaId: '',
  gsId: '',
  transactionSets: ['844', '849'],
  usageIndicator: 'P',
  ackRequested: true,
  active: true,
  notes: ''
};

const EDIConfigurations = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['ADMIN']);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try { setPartners(await getEdiPartners()); }
    catch (e) { toast.error('Failed to load EDI partners'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || '',
      partnerType: p.partnerType || 'WHOLESALER',
      isaQualifier: p.isaQualifier || 'ZZ',
      isaId: p.isaId || '',
      gsId: p.gsId || '',
      transactionSets: (p.transactionSets || '').split(',').map(s => s.trim()).filter(Boolean),
      usageIndicator: p.usageIndicator || 'P',
      ackRequested: !!p.ackRequested,
      active: !!p.active,
      notes: p.notes || ''
    });
    setShowForm(true);
  };

  const toggleTx = (t) => setForm(f => ({
    ...f,
    transactionSets: f.transactionSets.includes(t)
      ? f.transactionSets.filter(x => x !== t)
      : [...f.transactionSets, t]
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.isaId.trim()) return toast.error('Interchange ID is required');
    setSaving(true);
    const payload = { ...form, transactionSets: form.transactionSets.join(',') };
    try {
      if (editingId) { await updateEdiPartner(editingId, payload); toast.success('Configuration updated'); }
      else { await createEdiPartner(payload); toast.success('Configuration added'); }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save configuration');
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete EDI partner "${p.name}"? This also removes its field mappings.`)) return;
    try { await deleteEdiPartner(p.id); toast.success('Configuration deleted'); await load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to delete configuration'); }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">EDI Configurations</h3>
          <p className="text-sm text-gray-500">Trading partners and their X12 interchange envelope settings (ISA/GS identifiers, enabled transaction sets, usage).</p>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>
          {canEdit && (
            <button onClick={openAdd} title="Add Configuration" className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
              <Plus className="h-4 w-4 mr-2" /> Add Configuration
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            {['Partner', 'Type', 'Qualifier / ID', 'Transaction Sets', 'Usage', '997', 'Status', ''].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr></thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : partners.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                <Server className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                No trading partners configured yet.{canEdit ? ' Click "Add Configuration" to create one.' : ''}
              </td></tr>
            ) : partners.map(p => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}{p.notes ? <p className="text-xs text-gray-400 font-normal">{p.notes}</p> : null}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{p.partnerType}</td>
                <td className="px-4 py-3 text-sm text-gray-500 font-mono">{p.isaQualifier}:{p.isaId}{p.gsId ? <span className="text-gray-400"> / {p.gsId}</span> : null}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{p.transactionSets}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{p.usageIndicator === 'P' ? 'Production' : 'Test'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{p.ackRequested ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${p.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                  {canEdit && (
                    <>
                      <button onClick={() => openEdit(p)} className="text-gray-500 hover:text-primary-700 mr-3" title="Edit"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => remove(p)} className="text-gray-500 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">{editingId ? 'Edit' : 'Add'} EDI Configuration</h3>
              <button onClick={() => setShowForm(false)} title="Close" className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Partner Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="e.g. McKesson" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partner Type</label>
                  <select value={form.partnerType} onChange={e => setForm({ ...form, partnerType: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    {PARTNER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usage Indicator</label>
                  <select value={form.usageIndicator} onChange={e => setForm({ ...form, usageIndicator: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="P">Production (P)</option>
                    <option value="T">Test (T)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ISA Qualifier</label>
                  <input maxLength={2} value={form.isaQualifier} onChange={e => setForm({ ...form, isaQualifier: e.target.value.toUpperCase() })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="ZZ" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interchange ID (ISA) *</label>
                  <input maxLength={15} value={form.isaId} onChange={e => setForm({ ...form, isaId: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="up to 15 chars" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Code (GS02/GS03)</label>
                <input value={form.gsId} onChange={e => setForm({ ...form, gsId: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="optional" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Enabled Transaction Sets</label>
                <div className="flex flex-wrap gap-3">
                  {TX_SETS.map(t => (
                    <label key={t} className="inline-flex items-center text-sm text-gray-700">
                      <input type="checkbox" checked={form.transactionSets.includes(t)} onChange={() => toggleTx(t)} className="mr-1.5 rounded" /> {t}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input type="checkbox" checked={form.ackRequested} onChange={e => setForm({ ...form, ackRequested: e.target.checked })} className="mr-2 rounded" /> Request 997 acknowledgment
                </label>
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="mr-2 rounded" /> Active
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end space-x-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => setShowForm(false)} title="Cancel" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} title={editingId ? 'Save Changes' : 'Add Configuration'} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">{saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Configuration')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EDIConfigurations;
